import type { ReactNode } from 'react';
import React from 'react';
import { Typography } from '@mui/material';
import { Answer, AnswerLead, AnswerP, AnswerLink, AnswerList, AnswerLi } from '../../components/faq-accordion/answer';

export interface FaqItem {
  id: string;
  question: string;
  answer: ReactNode;
}

export interface FaqCategory {
  id: string;
  title: string;
  items: readonly FaqItem[];
}

export const faqs: readonly FaqCategory[] = [
  {
    id: 'payments-security',
    title: 'Payments and Security',
    items: [
      {
        id: 'payment-method',
        question: 'Payment Method',
        answer: (
          <Answer>
            <AnswerLead>What payment methods do you accept?</AnswerLead>
            <AnswerP>We currently accept all major credit cards: Visa, MasterCard, AmEx, and Discover.</AnswerP>
            <AnswerP>We&apos;re actively working on adding more payment options so you can pay the way that works best for you.</AnswerP>
          </Answer>
        ),
      },
      {
        id: 'payment-security',
        question: 'Payment Security',
        answer: (
          <Answer>
            <AnswerLead>Is my information safe when I make a payment on your site?</AnswerLead>
            <AnswerP>
              Yes. We use the same SSL encryption banks rely on, so your payment information stays between you and the bank. Nothing is stored on our servers. That means you can check out without worrying about your details getting into the wrong hands.
            </AnswerP>
            <AnswerP>
              If you have any questions or need assistance with your order, don’t hesitate to reach out to us at
              {' '}<AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'declined-payment',
        question: 'Declined Payment',
        answer: (
          <Answer>
            <AnswerLead>Why was my payment declined?</AnswerLead>
            <AnswerP>
              Most declines happen because your bank flagged the charge for extra security. It’s their way of keeping your account safe. The quickest fix is calling your bank for clarification or trying a different card.
            </AnswerP>
            <AnswerP>
              If you need any assistance with placing your order, don’t hesitate to contact us at{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'international-fees',
        question: 'International Fees',
        answer: (
          <Answer>
            <AnswerLead>Will I be charged any additional fees, like international or conversion charges?</AnswerLead>
            <AnswerP>
              We never add extra fees to your order. But if you’re outside the U.S., your bank might charge currency conversion or international transaction fees to your purchase.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'payment-update',
        question: 'Payment Update',
        answer: (
          <Answer>
            <AnswerLead>How can I update my credit card on file?</AnswerLead>
            <AnswerP>
              Email us at{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}
              and we’ll send you a secure link to update your card details. It only takes a minute and keeps your account active without interruption.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'trouble-payment',
        question: 'Trouble Making Payment',
        answer: (
          <Answer>
            <AnswerLead>Where can I get help if I’m having trouble with payment?</AnswerLead>
            <AnswerP>
              You can always reach our support team at{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>. We’ll be happy to walk you through the checkout or answer any concerns.
            </AnswerP>
          </Answer>
        ),
      },
    ]
  },
  {
    id: 'account-management',
    title: 'Account Management',
    items: [
      {
        id: 'did-not-receive-logins',
        question: 'Did Not Receive Logins',
        answer: (
          <Answer>
            <AnswerLead>Where are my login credentials? I haven’t received them.</AnswerLead>
            <AnswerP>
              Your login email usually arrives within minutes of your purchase. If you don’t see it:
            </AnswerP>
            <AnswerList>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Check your spam or junk folder</Typography>
                <Typography component="span" sx={{ fontWeight: 400 }}>{' - the email may have been filtered.'}</Typography>
              </AnswerLi>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Search your inbox for “Performance Golf”</Typography>
                <Typography component="span" sx={{ fontWeight: 400 }}>{' - look for the subject line '}</Typography>
                <Typography component="span" sx={{ fontStyle: 'italic', fontWeight: 400 }}>“Create Account”</Typography>
                <Typography component="span" sx={{ fontWeight: 400 }}>{'. This email contains your username and a link to set your password.'}</Typography>
              </AnswerLi>
            </AnswerList>
            <AnswerP>
              Still can’t find it? Email us at{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}with the name and email you used at checkout, and we’ll send your login right away so you can get started.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'password-reset',
        question: 'Password Reset',
        answer: (
          <Answer>
            <AnswerLead>How do I reset or change my password?</AnswerLead>
            <AnswerP>Resetting your password is quick and easy.</AnswerP>
            <AnswerP>
              Click here to reset it anytime: {' '}
              <Typography component="span" sx={{ overflowWrap: 'anywhere', wordBreak: { xs: 'break-all', sm: 'break-word' }, whiteSpace: 'normal' }}>
                <AnswerLink href="https://members.performancegolf.com/forgot-password/">https://members.performancegolf.com/forgot-password/</AnswerLink>
              </Typography>
            </AnswerP>
            <AnswerP>
              <Typography component="span" sx={{ fontWeight: 700 }}>Already logged in?</Typography>{' '}Just head to{' '}
              <AnswerLink href="https://members.performancegolf.com/">https://members.performancegolf.com/</AnswerLink>, click your profile in the top right corner, then go to{' '}
              <Typography component="span" sx={{ fontWeight: 700 }}>User Preferences {'>'} Password</Typography>{' '}to make the change.
            </AnswerP>
            <AnswerP>
              <Typography component="span" sx={{ fontWeight: 700 }}>Still need help?</Typography>{' '}Reach out to us at{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}- we’re happy to assist!
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'how-to-access-course',
        question: 'How To Access Course',
        answer: (
          <Answer>
            <AnswerLead>How do I access my training program?</AnswerLead>
            <AnswerP>Your training is ready as soon as you complete your purchase.</AnswerP>
            <AnswerP>
              Just head to{' '}
              <AnswerLink href="https://performancegolf.com/membership">performancegolf.com/membership</AnswerLink>, log in, and click on the TRAINING tab. Use the FILTER tool to easily find your program by Course Type or Instructor.
            </AnswerP>
            <AnswerP>
              <Typography component="span" sx={{ fontWeight: 700 }}>Still need help?</Typography>{' '}Reach out to us at{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}- we’re here to help!
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'login-trouble',
        question: 'Login Trouble',
        answer: (
          <Answer>
            <AnswerLead>I’m having trouble logging in. What should I do?</AnswerLead>
            <AnswerP>Most login issues clear up with a quick browser check.</AnswerP>
            <AnswerP>
              <Typography component="span" sx={{ fontWeight: 700 }}>For Web Users:</Typography>
            </AnswerP>
            <AnswerList>
              <AnswerLi>
                Update your browser at{' '}
                <AnswerLink href="https://browsehappy.com">browsehappy.com</AnswerLink>
              </AnswerLi>
              <AnswerLi>Try another browser like Chrome, Safari, or Firefox</AnswerLi>
              <AnswerLi>Clear your cache and cookies</AnswerLi>
            </AnswerList>
            <AnswerP>
              If you still can’t get in, email us at{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}and we’ll help you log in right away.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'videos-wont-play',
        question: "Videos Won't Play",
        answer: (
          <Answer>
            <AnswerLead>My videos won’t play. What should I do?</AnswerLead>
            <AnswerP>Most playback issues clear up fast with a quick reset:</AnswerP>
            <AnswerList>
              <AnswerLi>
                Update your browser at{' '}
                <AnswerLink href="https://browsehappy.com">browsehappy.com</AnswerLink>
              </AnswerLi>
              <AnswerLi>Clear your cache and cookies</AnswerLi>
              <AnswerLi>Try a different browser</AnswerLi>
              <AnswerLi>Restart your device</AnswerLi>
            </AnswerList>
            <AnswerP>
              If it’s still giving you trouble, email{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}and we’ll get it fixed so you can get back to your training.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'privacy-settings',
        question: 'Privacy Settings',
        answer: (
          <Answer>
            <AnswerLead>I can't access my videos due to privacy settings. What should I do?</AnswerLead>
            <AnswerP>
              Try these quick fixes:
            </AnswerP>
            <AnswerList>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Adjust Privacy Settings:</Typography>{' '}
                Try temporarily disabling “Do Not Track” settings or any privacy-focused browser extensions.
              </AnswerLi>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Switch Browsers:</Typography>{' '}
                Sometimes, using a different browser (e.g., Chrome, Safari, Firefox) resolves the issue.
              </AnswerLi>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Check Antivirus Software:</Typography>{' '}
                Some antivirus programs may block content. Make sure it’s not restricting access.
              </AnswerLi>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Allow Cookies:</Typography>{' '}
                Navigate to your browser's privacy settings (often under a shield icon or "Cookie Control") and select “Allow All Cookies.”
              </AnswerLi>
            </AnswerList>
            <AnswerP>
              If the issue persists, feel free to contact our support team at{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>.
            </AnswerP>
          </Answer>
        ),
      },
    ]
  },
  {
    id: 'refunds',
    title: 'Refunds',
    items: [
      {
        id: 'refund-posting',
        question: 'Refund Posting',
        answer: (
          <Answer>
            <AnswerLead>How long does it take for my refund to post?</AnswerLead>
            <AnswerP>
              Most refunds show up within 3–10 business days, depending on your bank or card provider. If it’s taking longer, your bank can give you the fastest update.
            </AnswerP>
            <AnswerP>
              Still have concerns? Contact us at{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}- we’re happy to help!
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'refund-destination',
        question: 'Refund Destination',
        answer: (
          <Answer>
            <AnswerLead>Can you issue a refund to a different card?</AnswerLead>
            <AnswerP>
              For security and compliance reasons, refunds have to go back to the card used at checkout. If that card’s been replaced or closed, your bank will usually route the refund to your new account.
            </AnswerP>
            <AnswerP>
              If that card has been replaced or closed, your bank should still process the refund and transfer it to your new account. We recommend confirming this with your bank to avoid any issues.
            </AnswerP>
            <AnswerP>
              Still need help? Reach out to us at{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'refund-request',
        question: 'Refund Request',
        answer: (
          <Answer>
            <AnswerLead>Can I get a refund for my order?</AnswerLead>
            <AnswerP>
              Yes. A majority of our training programs and physical products come with a 365-day money-back guarantee.
            </AnswerP>
            <AnswerP>
              If it’s not working for you, email{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}and we’ll refund you.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'refund-not-received',
        question: 'Did Not Receive Refund',
        answer: (
          <Answer>
            <AnswerLead>I haven’t received my refund. What should I do?</AnswerLead>
            <AnswerP>
              Most refunds show up within 3–10 business days, depending on your bank. If it’s been longer, your bank can give you the quickest update.
            </AnswerP>
            <AnswerP>
              If you still need help, email{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}and we’ll make sure it’s sorted.
            </AnswerP>
          </Answer>
        ),
      }
    ]
  },
  {
    id: 'shipping-delivery',
    title: 'Shipping And Delivery',
    items: [
      {
        id: 'order-shipment',
        question: 'Order Shipment',
        answer: (
          <Answer>
            <AnswerLead>When will my order ship?</AnswerLead>
            <AnswerP>
              In-stock items usually leave our warehouse within 1–2 business days. Once it ships, you’ll receive a{' '}
              <Typography component="span" sx={{ fontWeight: 700 }}>shipping confirmation email</Typography>{' '}with tracking details so you can follow your delivery every step of the way.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'shipping-timeframe',
        question: 'Shipping Timeframe',
        answer: (
          <Answer>
            <AnswerLead>How long does shipping take?</AnswerLead>
            <AnswerP>If you’re in the U.S., you’ll usually have your gear within 3–7 business days, depending on your location.</AnswerP>
            <AnswerP>Ordering from outside the U.S.? Delivery can take 7–21 business days, since customs and local carriers handle the final leg.</AnswerP>
          </Answer>
        ),
      },
      {
        id: 'international-shipping',
        question: 'International Shipping',
        answer: (
          <Answer>
            <AnswerLead>Do you ship internationally?</AnswerLead>
            <AnswerP>Yes. If you’re in Canada, Australia, Germany, New Zealand, or the United Kingdom, we can ship your order straight to you.</AnswerP>
            <AnswerP>If your country isn’t listed, we’re adding more locations and will share updates as soon as new destinations open up.</AnswerP>
          </Answer>
        ),
      },
      {
        id: 'tracking-number',
        question: 'Tracking Number',
        answer: (
          <Answer>
            <AnswerLead>How can I track my order?</AnswerLead>
            <AnswerP>You’ll get a confirmation email with a tracking link as soon as your order ships.</AnswerP>
            <AnswerP>
              Can’t find it? Email{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}and we’ll send it again so you can follow your delivery.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'delivered-not-received',
        question: 'Delivered But Not Received',
        answer: (
          <Answer>
            <AnswerLead>My package says it was delivered, but I didn’t receive it.</AnswerLead>
            <AnswerP>Oh no, that’s frustrating. First, check with neighbors or anyone in your household in case they grabbed it for you.</AnswerP>
            <AnswerP>
              If it’s still missing after a couple of days, email{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}and we’ll jump in to help track it down.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'expedited-shipping',
        question: 'Expedited Shipping',
        answer: (
          <Answer>
            <AnswerLead>Do you offer expedited shipping?</AnswerLead>
            <AnswerP>
              Yes. If your order is shipping within the United States and the items are in stock, you’ll see these expedited options at checkout:
            </AnswerP>
            <AnswerList>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>3-Day Priority Shipping</Typography>{' '}– $55
              </AnswerLi>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>2-Day Priority Shipping</Typography>{' '}– $70
              </AnswerLi>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Next-Day Priority Shipping</Typography>{' '}– $120
              </AnswerLi>
            </AnswerList>
            <AnswerP>Right now, expedited shipping isn’t available for Canada, Australia, or the United Kingdom.</AnswerP>
          </Answer>
        ),
      },
    ]
  },
  {
    id: 'returns-exchanges',
    title: 'Returns and Exchanges',
    items: [
      // {
      //   id: '365-mbg',
      //   question: '365 MBG',
      //   answer: (
      //     <Answer>
      //       <AnswerLead>What’s your return policy?</AnswerLead>
      //       <AnswerP>
      //         Every order is covered by our 365-day money-back guarantee. If it’s not working for you, email{' '}
      //         <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}and we’ll make it right with no questions asked.
      //       </AnswerP>
      //     </Answer>
      //   ),
      // },
      {
        id: 'return-instructions',
        question: 'Return Instructions',
        answer: (
          <Answer>
            <AnswerLead>How do I return a product?</AnswerLead>
            <AnswerP>
              Email{' '}<AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}with your order number and why you’re returning it. We’ll send you simple, step-by-step instructions and a return authorization number so you can get your refund quickly and be free to choose something that’s a better fit for you.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'return-shipping-fee',
        question: 'Return Shipping Fee',
        answer: (
          <Answer>
            <AnswerLead>Do I have to pay for return shipping?</AnswerLead>
            <AnswerP>
              If we made an error with your order, we’ll cover the return shipping so you’re never out of pocket for our mistake.
            </AnswerP>
            <AnswerP>
              For all other returns, shipping costs are the customer’s responsibility. Email{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}and we’ll help you set it up in the easiest, most affordable way possible.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'replacement-request',
        question: 'Replacement Request',
        answer: (
          <Answer>
            <AnswerLead>Can I get a replacement for my item?</AnswerLead>
            <AnswerP>
              Yes. If something’s wrong with your order, we’ll send the correct item at no cost to you.
            </AnswerP>
            <AnswerP>
              Email{' '}<AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}with your order number, a short description of the problem, and photos if you have them. We’ll get your replacement out fast so you can start using it right away.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'damaged-item',
        question: 'Damaged Item',
        answer: (
          <Answer>
            <AnswerLead>What should I do if my club arrives damaged?</AnswerLead>
            <AnswerP>
              That’s not how your new club should show up. Email{' '}<AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}within 7 days of delivery with your order number, a short description of the damage, and a few photos of the club and packaging.
            </AnswerP>
            <AnswerP>
              Once we have that, we’ll get a replacement sent out quickly so you can start playing with it as soon as possible.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'returned-item',
        question: 'Returned Item',
        answer: (
          <Answer>
            <AnswerLead>I returned my item. When will I get my refund or replacement?</AnswerLead>
            <AnswerP>
              We’ll process your refund or replacement as soon as your return is received at our warehouse.
            </AnswerP>
            <AnswerP>
              To help speed things up, feel free to email a copy of your{' '}
              <Typography component="span" sx={{ fontWeight: 700 }}>return tracking number</Typography>{' '}to{' '}
              <AnswerLink href="mailto:support@performancegolfzone.com">support@performancegolfzone.com</AnswerLink>{' '}- we’ll take it from there!
            </AnswerP>
          </Answer>
        ),
      },
    ]
  },
  {
    id: 'digital-products',
    title: 'Digital Products',
    items: [
      {
        id: 'download-option-not-available',
        question: 'Download Option Not Available',
        answer: (
          <Answer>
            <AnswerLead>Can I download my training videos?</AnswerLead>
            <AnswerP>
              Not right now, since the files are huge and we keep the content updated. The good news is you’ll have lifetime online access, so you can watch anytime on any device.
            </AnswerP>
            <AnswerP>
              Already purchased? Log in at{' '}
              <AnswerLink href="https://performancegolf.com/membership">performancegolf.com/membership</AnswerLink>. To find your course quickly, head to the TRAINING tab and use the FILTER to sort by Course Type or Instructor.
            </AnswerP>
            <AnswerP>
              Haven’t purchased yet? Browse our full course library here:{' '}
              <AnswerLink href="https://performancegolf.com/training">performancegolf.com/training</AnswerLink>
            </AnswerP>
            <AnswerP>
              Still need help? We’ve got your back — email us at{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'course-refund',
        question: 'Course Refund',
        answer: (
          <Answer>
            <AnswerLead>Your program did not improve my game. Refund me.</AnswerLead>
            <AnswerP>
              Every training program is covered by our 365-day money-back guarantee. If it didn’t help your game, email{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}and we’ll refund you, no questions asked.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'cancel-subscription',
        question: 'Cancellation Of Subscription',
        answer: (
          <Answer>
              <AnswerLead>How do I cancel my subscription?</AnswerLead>
              <AnswerP>Canceling is quick and easy:</AnswerP>
              <AnswerList>
              <AnswerLi>
                {' '}<AnswerLink href="https://members.performancegolf.com/account/subscriptions">Click here</AnswerLink> to access your subscriptions portal
              </AnswerLi>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>If you cancel, your access will remain active until the end of your current billing cycle.</Typography> You’ll get a confirmation email once it’s canceled.
              </AnswerLi>
              </AnswerList>
              <AnswerP>
                        Need help? Email us at{' '}<AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}— we’re happy to assist.
              </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'left-handed',
        question: 'Program For Left Handed',
        answer: (
          <Answer>
            <AnswerLead>Do your training programs work for left-handed golfers?</AnswerLead>
            <AnswerP>
              Yes. Most of our digital training works whether you play right or left-handed, because the drills and techniques are built to fit either side. If a program ever has exceptions, you’ll see it clearly noted in the course details before you buy.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'vip-upload',
        question: 'How To Upload Swing Videos To VIP Coaching',
        answer: (
          <Answer>
            <AnswerLead>How Do I Upload Videos to VIP Coaching?</AnswerLead>
            <AnswerP>Uploading your swing video is simple:</AnswerP>
            <AnswerList>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Record your swing</Typography>{' '}– Need help? Watch our guide:{' '}
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  <AnswerLink href="https://vimeo.com/895915480/ec4f8e16da?share=copy">How to Properly Record Your Golf Swing</AnswerLink>
                </Typography>
              </AnswerLi>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Log in to your VIP Coaching portal</Typography>
              </AnswerLi>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Upload your video</Typography>{' '}directly through the portal for review
              </AnswerLi>
            </AnswerList>
            <AnswerP>
              Your coach will analyze your swing and provide personalized feedback to help you improve faster.
            </AnswerP>
            <AnswerP>
              Need assistance? Reach out anytime at{' '}<AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}- we’re here to help!
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'vip-video-limit',
        question: 'VIP Coaching Video Limit',
        answer: (
          <Answer>
            <AnswerLead>How many videos can I upload each month?</AnswerLead>
            <AnswerP>
              With VIP Coaching, you can upload as many videos as you want. For the fastest uploads, send them one at a time so the files don’t get bogged down.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'vip-response-time',
        question: 'VIP Coach Response Time',
        answer: (
          <Answer>
            <AnswerLead>I uploaded a video but haven’t heard back from my coach.</AnswerLead>
            <AnswerP>
              Your VIP Coach usually replies within 3–5 business days so they have time to study your swing and send back a personalized video with clear, actionable feedback.
            </AnswerP>
            <AnswerP>
              If it’s been longer, email{' '}<AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}and we’ll check in to make sure your submission is on track.
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'swingai-upload-issue',
        question: 'SwingAI Upload Issue',
        answer: (
          <Answer>
            <AnswerLead>Trouble Uploading or Analyzing SwingAI Videos?</AnswerLead>
            <AnswerP>
              If you're having issues uploading or analyzing your swing in SwingAI, try these quick tips:
            </AnswerP>
            <AnswerList>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Keep your full body in frame</Typography>{' '}– including hands. Position the camera about 8 feet away.
              </AnswerLi>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Use good lighting</Typography>{' '}– avoid strong backlighting.
              </AnswerLi>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Wear contrasting clothes</Typography>{' '}– stand out from your background.
              </AnswerLi>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Record at least 4 seconds</Typography>{' '}– shorter clips may fail to process.
              </AnswerLi>
              <AnswerLi>
                <Typography component="span" sx={{ fontWeight: 700 }}>Use SwingAI’s built-in recorder</Typography>{' '}– for best results.
              </AnswerLi>
            </AnswerList>
            <AnswerP>
              Still not working? Send a sample video to{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>, and we’ll take a closer look!
            </AnswerP>
          </Answer>
        ),
      },
      {
        id: 'score-tracker',
        question: 'Adding Score on Score Tracker',
        answer: (
          <Answer>
            <AnswerLead>How Do I Add a Score to the Score Tracker?</AnswerLead>
            <AnswerP>
              Score Tracker is a great tool to monitor your progress and key stats like total score, putts, fairways hit, and more. Here’s how to do it so you can continue improving your game.
            </AnswerP>
            <AnswerList>
              <AnswerLi>
                Go to{' '}<AnswerLink href="https://members.performancegolf.com">members.performancegolf.com</AnswerLink>{' '}and log in.
              </AnswerLi>
              <AnswerLi>
                Navigate to <Typography component="span" sx={{ fontWeight: 700 }}>Scratch Club Area</Typography> → Click <Typography component="span" sx={{ fontWeight: 700 }}>Record & Track</Typography>.
              </AnswerLi>
              <AnswerLi>
                Click <Typography component="span" sx={{ fontWeight: 700 }}>Submit Scorecard</Typography> and fill in:
                <AnswerList>
                  <AnswerLi>
                    <Typography component="span" sx={{ fontWeight: 700 }}>Date of round</Typography>
                  </AnswerLi>
                  <AnswerLi>
                    <Typography component="span" sx={{ fontWeight: 700 }}>Course Name</Typography>
                  </AnswerLi>
                  <AnswerLi>
                    <Typography component="span" sx={{ fontWeight: 700 }}>Tee selection</Typography>
                  </AnswerLi>
                  <AnswerLi><Typography component="span" sx={{ fontWeight: 700 }}>Your Score</Typography></AnswerLi>
                </AnswerList>
              </AnswerLi>
              <AnswerLi>
                Click <Typography component="span" sx={{ fontWeight: 700 }}>Submit</Typography> to save.
              </AnswerLi>
            </AnswerList>
            <AnswerP>
              If you're having trouble uploading your scores, just email your details to{' '}
              <AnswerLink href="mailto:support@performancegolf.com">support@performancegolf.com</AnswerLink>{' '}and our team will log them for you.
            </AnswerP>
          </Answer>
        ),
      },
    ]
  }
] as const;